import { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useMediaQuery } from '@mui/material';

import usePeerActions from '@src/hooks/usePeerActions';

import SocketService from '@src/services/SocketService';

import {
  useConsumersStore,
  useDrawerStore,
  usePeersStore,
  usePollsStore,
  useVideoStreamStore,
  useRoomStore,
  useNotificationStore,
  useRoomConfigStore,
  useEmojiStore,
  TLastEmoji,
} from '@src/stores';

import {
  IPeer,
  ISocketCmd,
  TUserType,
  TMediaRequest,
  TPeerActionCallbackProps,
  EUserType,
  EMeetingType,
  EMessageType,
  IHandleUpdatePeerInfo,
  IVideoStream,
  ECommonSocketKeys,
} from '@src/types';
type TParticipant = IPeer & { isMe?: boolean };

interface MeetRoomContextType {
  isLoading: boolean;
  isFullyJoined: boolean;
  currentPeer?: IPeer;
  currentPeerType?: TUserType;
  isHostOrCohost: boolean;
  isHost: boolean;
  isActingHost: boolean;
  isAttendee: boolean;
  isPanelist: boolean;
  isWebinar: boolean;
  isWebinarAttendee: boolean;
  currentPeerId: string;
  isCurrentPeerSharing: boolean;
  shareScreenStream: IVideoStream | null;
  canAnnotate: boolean;
  participantsPeers: TParticipant[];
  thereAreParticipants: boolean;
  hasChatNotifications: boolean;
  hasActiveLimitedPolls: boolean;
  emoji?: TLastEmoji;
  isMobileDevice: boolean;
  isMobileAndTabletDevice: boolean;
  isLandscape: boolean;
  canStopStartAnnotating: boolean;

  setHasRequestedShare: (value: boolean) => void;
  setHasTempShareAccess: (value: boolean) => void;
  setHasUsedShareAccess: (value: boolean) => void;
  setIsCallEnded: (value: boolean) => void;

  emitCmd: (cmd: ISocketCmd) => void;
  handleCmd: (cmd: ISocketCmd) => void;
  peerAction: (props: TPeerActionCallbackProps) => void;
  handleUpdatePeerInfo: (props: IHandleUpdatePeerInfo) => void;
  getCurrentPeer: () => IPeer | undefined;
  setIsLoading: (isLoading: boolean) => void;

  isPublicChatEnabled: boolean;
  isPrivateChatEnabled: boolean;
  isPollEnabled: boolean;
  canUserShareScreen: boolean;
  canUserShareCalculator: boolean;
  isCalculatorEnabled: boolean;

  canUserRecord: boolean;
  canUserMute: boolean;
  canUserBan: boolean;
  canUserEject: boolean;
  canUserCreatePoll: boolean;
  canUserEndMeeting: boolean;
  canUserManageSettings: boolean;
  hasTempShareAccess: boolean;
  hasRequestedShare: boolean;
  isCallEnded: boolean;
  canUserAskToUnmute: boolean;
  micRequest: TMediaRequest;
  setMicRequest: (request: TMediaRequest) => void;
  camRequest: TMediaRequest;
  setCamRequest: (request: TMediaRequest) => void;
  processedMediaRequest: TMediaRequest;
  setProcessedMediaRequest: (request: TMediaRequest) => void;

  // refs
  recordedBlobsRef: React.MutableRefObject<BlobPart[]>;
  isRecordingRef: React.MutableRefObject<boolean>;
  audioRecorderRef: React.MutableRefObject<any>;
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>;
  recScreenStreamRef: React.MutableRefObject<MediaStream | null>;
  isAudioEventListenerActiveRef: React.MutableRefObject<boolean>;

  // buttons visibility
  isStartAudioButtonEnabled: boolean;
  isStartVideoButtonEnabled: boolean;
  isTranscriptionButtonEnabled: boolean;
  isEmojiRoomButtonEnabled: boolean;
  isShareButtonEnabled: boolean;
  isRaiseHandButtonEnabled: boolean;
  isExitButtonEnabled: boolean;
  isEditorButtonEnabled: boolean;
  isWhiteboardButtonEnabled: boolean;
  isParticipantsButtonEnabled: boolean;
  isThreeDotsFooterMenuEnabled: boolean;
  isPollDeleteButtonEnabled: boolean;
  isQuestionAndAnswerEnabled: boolean;

  isChatSpeechStartButtonEnabled: boolean;
  isChatEmojiButtonEnabled: boolean;
  isChatFileSharingButtonEnabled: boolean;

  speakersList: IPeer[];
}

const meetRoomContextDefaultValue: MeetRoomContextType = {
  isLoading: true,
  isFullyJoined: false,
  currentPeer: undefined,
  currentPeerId: '',
  isHostOrCohost: false,
  isHost: false,
  isActingHost: false,
  isWebinarAttendee: false,
  isWebinar: false,
  isAttendee: false,
  isPanelist: false,
  currentPeerType: EUserType.ATTENDEE,
  isCurrentPeerSharing: false,
  shareScreenStream: null,
  canAnnotate: false,
  participantsPeers: [],
  thereAreParticipants: false,
  hasChatNotifications: false,
  hasActiveLimitedPolls: false,
  emoji: undefined,
  isMobileDevice: false,
  isMobileAndTabletDevice: false,
  isLandscape: false,
  canStopStartAnnotating: false,

  setHasRequestedShare: () => {},
  setHasTempShareAccess: () => {},
  setHasUsedShareAccess: () => {},
  setIsCallEnded: () => {},

  emitCmd: () => {},
  handleCmd: () => {},
  peerAction: () => {},
  handleUpdatePeerInfo: () => {},
  setIsLoading: () => {},
  getCurrentPeer: () => undefined,

  isPublicChatEnabled: false,
  isPrivateChatEnabled: false,
  isPollEnabled: false,
  canUserShareScreen: false,
  canUserShareCalculator: false,
  isCalculatorEnabled: true,

  canUserRecord: false,
  canUserMute: false,
  canUserBan: false,
  canUserEject: false,
  canUserCreatePoll: false,
  canUserEndMeeting: false,
  canUserManageSettings: false,
  hasTempShareAccess: false,
  hasRequestedShare: false,
  isCallEnded: false,
  canUserAskToUnmute: false,

  micRequest: null,
  setMicRequest: () => {},
  camRequest: null,
  setCamRequest: () => {},
  processedMediaRequest: null, // in case we have new requests we can show one active at the time.
  setProcessedMediaRequest: () => {},

  // refs
  recordedBlobsRef: { current: [] },
  isRecordingRef: { current: false },
  audioRecorderRef: { current: null },
  mediaRecorderRef: { current: null },
  recScreenStreamRef: { current: null },
  isAudioEventListenerActiveRef: { current: true },

  // buttons visibility
  isStartAudioButtonEnabled: false,
  isStartVideoButtonEnabled: false,
  isTranscriptionButtonEnabled: false,
  isEmojiRoomButtonEnabled: false,
  isShareButtonEnabled: false,
  isRaiseHandButtonEnabled: false,
  isExitButtonEnabled: false,
  isEditorButtonEnabled: false,
  isWhiteboardButtonEnabled: false,
  isParticipantsButtonEnabled: false,
  isThreeDotsFooterMenuEnabled: false,
  isPollDeleteButtonEnabled: false,
  isQuestionAndAnswerEnabled: false,

  isChatEmojiButtonEnabled: false,
  isChatSpeechStartButtonEnabled: false,
  isChatFileSharingButtonEnabled: false,

  speakersList: [],
};

const MeetRoomContext = createContext<MeetRoomContextType>(meetRoomContextDefaultValue);

interface MeetRoomContextProviderProps {
  children: ReactNode;
}

export const MeetRoomContextProvider: React.FC<MeetRoomContextProviderProps> = ({ children }) => {
  const isMobileDevice = useMediaQuery('(max-width:768px)');
  const isMobileAndTabletDevice = useMediaQuery('(max-width:1024px)');
  const [isLoading, setIsLoading] = useState(true);
  const [isLandscape, setIsLandscape] = useState(false);

  const [hasRequestedShare, setHasRequestedShare] = useState(false);
  const [hasTempShareAccess, setHasTempShareAccess] = useState(false);
  const [hasUsedShareAccess, setHasUsedShareAccess] = useState(false);
  const [isCallEnded, setIsCallEnded] = useState(false);
  const [micRequest, setMicRequest] = useState<TMediaRequest>(null);
  const [camRequest, setCamRequest] = useState<TMediaRequest>(null);
  const [processedMediaRequest, setProcessedMediaRequest] = useState<TMediaRequest>(null);

  const isRecordingRef = useRef(false);
  const audioRecorderRef = useRef<any>(null);
  const recordedBlobsRef = useRef<BlobPart[]>([]);
  const isAudioEventListenerActiveRef = useRef(true);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recScreenStreamRef = useRef<MediaStream | null>(null);

  const { peers, updatePeerData } = usePeersStore(
    useShallow((state) => ({
      peers: state.peers,
      updatePeerData: state.updatePeerData,
    }))
  );

  const { videoStreams } = useVideoStreamStore(
    useShallow((state) => ({
      videoStreams: state.videoStreams,
    }))
  );
  const { meetingType, isActingHost } = useRoomStore(
    useShallow((state) => ({
      meetingType: state.meetingType,
      isActingHost: state.isActingHost,
    }))
  );

  const isFullyJoined = useMemo(() => videoStreams.some((video) => video.isCurrentUser), [videoStreams]);

  const {
    participantsListConfig,
    pollConfig,
    mainConfig,
    chatConfig,
    settingsConfig,
    calculatorConfig,
    isFloatingButtonEnabled,
    isShowHideButtonEnabled,
  } = useRoomConfigStore(
    useShallow((state) => ({
      participantsListConfig: state.participantsListConfig,
      pollConfig: state.pollConfig,
      mainConfig: state.mainConfig,
      chatConfig: state.chatConfig,
      settingsConfig: state.settingsConfig,
      calculatorConfig: state.calculatorConfig,
      isFloatingButtonEnabled: state.magicPenConfig?.floatingButton,
      isShowHideButtonEnabled: state.magicPenConfig?.showHideButton,
    }))
  );

  // compute current peer and type
  const socketId = SocketService.getSocketId();

  const currentPeer = useMemo(() => {
    return socketId ? peers.get(socketId) : undefined;
  }, [peers, socketId]);

  const currentPeerId = useMemo(() => currentPeer?.id || '', [currentPeer?.id]);
  const currentPeerType = useMemo<TUserType>(
    () => currentPeer?.peer_info.user_type ?? EUserType.ATTENDEE,
    [currentPeer?.peer_info.user_type]
  );
  const isHostOrCohost = useMemo(() => {
    return !!currentPeerType && [EUserType.HOST, EUserType.CO_HOST].includes(currentPeerType);
  }, [currentPeerType]);

  const isHost = useMemo(() => {
    return currentPeerType === EUserType.HOST;
  }, [currentPeerType]);

  const isAttendee = useMemo(() => {
    return currentPeerType === EUserType.ATTENDEE;
  }, [currentPeerType]);

  const isWebinarAttendee = useMemo(
    () => currentPeerType === EUserType.ATTENDEE && meetingType === EMeetingType.WEBINAR,
    [currentPeerType, meetingType]
  );

  const isWebinar = useMemo(() => meetingType === EMeetingType.WEBINAR, [meetingType]);

  const isPanelist = useMemo(() => {
    return (
      currentPeerType === EUserType.SPEAKER ||
      currentPeerType === EUserType.CO_HOST ||
      currentPeerType === EUserType.HOST
    );
  }, [currentPeerType]);

  const speakersList = useMemo(() => {
    return [...peers.values()].filter((peer) => peer.peer_info.user_type === EUserType.SPEAKER);
  }, [peers]);

  // Room settings (controlled from settings panel)
  const {
    isPublicChatEnabled,
    isPrivateChatEnabled,
    isPollEnabled,
    canUserShareScreen,
    canUserShareCalculator,
    isCalculatorEnabled,
  } = useMemo(() => {
    if (!currentPeerType) {
      return {
        isPublicChatEnabled: false,
        isPrivateChatEnabled: false,
        isPollEnabled: false,
        canUserShareScreen: false,
        canUserShareCalculator: false,
        isCalculatorEnabled: true,
      };
    }
    return {
      isPublicChatEnabled: mainConfig.chatButton,
      isPrivateChatEnabled: chatConfig.privateChatButton,
      isPollEnabled: mainConfig.pollButton,
      canUserShareScreen: mainConfig.startScreenButton,
      canUserShareCalculator: calculatorConfig?.shareCalculatorButton,
      isCalculatorEnabled: calculatorConfig?.calculatorShowButton,
    };
  }, [currentPeerType, mainConfig, chatConfig, calculatorConfig]);

  // Permissions
  const userPermissions = useMemo(
    () => ({
      canUserBan: participantsListConfig.banButton,
      canUserEndMeeting: mainConfig.endMeetingForAll,
      canUserCreatePoll: pollConfig.pollCreateButton,
      canUserMute: participantsListConfig.muteButton,
      canUserManageSettings: mainConfig.settingsButton,
      canUserEject: participantsListConfig.ejectButton,
      canUserAskToUnmute: participantsListConfig.unMuteButton,
    }),
    [participantsListConfig, mainConfig, pollConfig]
  );

  // Footer Buttons
  const footerButtons = useMemo(
    () => ({
      isStartAudioButtonEnabled: mainConfig.startAudioButton,
      isStartVideoButtonEnabled: mainConfig.startVideoButton,
      isTranscriptionButtonEnabled: mainConfig.transcriptionButton,
      isEmojiRoomButtonEnabled: mainConfig.emojiRoomButton,
      isShareButtonEnabled: mainConfig.shareButton,
      isRaiseHandButtonEnabled: mainConfig.raiseHandButton,
      isExitButtonEnabled: mainConfig.exitButton,
      isEditorButtonEnabled: mainConfig.editorButton,
      isWhiteboardButtonEnabled: mainConfig.whiteboardButton,
      isQuestionAndAnswerEnabled: mainConfig.questionsButton,
      isParticipantsButtonEnabled: mainConfig.participantsButton ?? true,
      isThreeDotsFooterMenuEnabled: mainConfig.threeDotsFooterMenu,
    }),
    [mainConfig]
  );

  // Poll Buttons
  const pollButtons = useMemo(
    () => ({
      isPollDeleteButtonEnabled: pollConfig.pollDeleteButton,
    }),
    [pollConfig]
  );

  // Settings Buttons
  const settingsButtons = useMemo(
    () => ({
      isCameraOptionsButtonEnabled: settingsConfig.cameraOptionsButton,
      isMicOptionsButtonEnabled: settingsConfig.micOptionsButton,
      isSpeakerOptionsButtonEnabled: settingsConfig.speakerOptionsButton,
    }),
    [settingsConfig]
  );

  // Others
  const { canUserRecord, isChatEmojiButtonEnabled, isChatFileSharingButtonEnabled, isChatSpeechStartButtonEnabled } =
    useMemo(() => {
      if (!currentPeerType) {
        return {
          canUserRecord: false,
          isChatEmojiButtonEnabled: false,
          isChatFileSharingButtonEnabled: false,
          isChatSpeechStartButtonEnabled: false,
        };
      }
      return {
        isChatEmojiButtonEnabled: chatConfig.chatEmojiButton,
        isChatFileSharingButtonEnabled: chatConfig.chatFileSharingButton,
        isChatSpeechStartButtonEnabled: chatConfig.chatSpeechStartButton,
        canUserRecord: mainConfig.startRecordingButton,
      };
    }, [currentPeerType, mainConfig, chatConfig]);

  const {
    canUserBan,
    canUserMute,
    canUserEject,
    canUserCreatePoll,
    canUserEndMeeting,
    canUserAskToUnmute,
    canUserManageSettings,
  } = userPermissions;

  const {
    isStartAudioButtonEnabled,
    isStartVideoButtonEnabled,
    isTranscriptionButtonEnabled,
    isEmojiRoomButtonEnabled,
    isShareButtonEnabled,
    isRaiseHandButtonEnabled,
    isExitButtonEnabled,
    isEditorButtonEnabled,
    isWhiteboardButtonEnabled,
    isParticipantsButtonEnabled,
    isThreeDotsFooterMenuEnabled,
    isQuestionAndAnswerEnabled,
  } = footerButtons;

  const { isPollDeleteButtonEnabled } = pollButtons;

  //for future use, if needed
  const { isCameraOptionsButtonEnabled, isMicOptionsButtonEnabled, isSpeakerOptionsButtonEnabled } = settingsButtons;

  const { toggleDrawer, setSelectedKey } = useDrawerStore(
    useShallow((state) => ({
      toggleDrawer: state.toggleDrawer,
      setSelectedKey: state.setSelectedKey,
    }))
  );

  const { consumers } = useConsumersStore(
    useShallow((state) => ({
      consumers: state.consumers,
    }))
  );

  const { polls } = usePollsStore(
    useShallow((state) => ({
      polls: state.polls,
    }))
  );

  const { isJoinedRoom, hasMagicPenAnnotatePermission } = useRoomStore(
    useShallow((state) => ({
      isJoinedRoom: state.isJoinedRoom,
      hasMagicPenAnnotatePermission: state.hasMagicPenAnnotatePermission,
    }))
  );

  const { messagesNotifications } = useNotificationStore(
    useShallow((state) => ({
      messagesNotifications: state.messagesNotifications,
    }))
  );

  const thereAreParticipants = useMemo(() => consumers.size > 0 || peers?.size > 1, [consumers.size, peers?.size]);

  const participantsPeers = useMemo(() => {
    return [...peers.values()].reduce((acc, peer) => {
      const isMe = peer.id === currentPeerId;

      if (isWebinarAttendee && isMe) {
        return acc;
      }

      if (isMe) {
        acc.unshift({ ...peer, isMe: true });
      } else {
        acc.push({ ...peer, isMe: false });
      }

      return acc;
    }, [] as TParticipant[]);
  }, [currentPeerId, peers, isWebinarAttendee]);

  const hasActiveLimitedPolls = useMemo(
    () =>
      !canUserCreatePoll &&
      !!polls.some(({ isVoteClosed, closingTime }) => closingTime !== 'none' && !isVoteClosed) &&
      isPollEnabled,
    [canUserCreatePoll, polls, isPollEnabled]
  );

  const isCurrentPeerSharing = useMemo(() => {
    return !!peers.get(currentPeerId)?.peer_info.peer_screen;
  }, [currentPeerId, peers]);

  const shareScreenStream = useMemo<IVideoStream | null>(
    () => videoStreams.find(({ type }) => type === 'screenType') ?? null,
    [videoStreams]
  );

  const canAnnotate = useMemo(() => {
    // Explicit Magic Pen annotate permission (granted by host) always enables annotation
    if (hasMagicPenAnnotatePermission) {
      return true;
    }

    return isCurrentPeerSharing || isHost || isActingHost || isFloatingButtonEnabled;
  }, [hasMagicPenAnnotatePermission, isCurrentPeerSharing, isHost, isActingHost, isFloatingButtonEnabled]);

  const canStopStartAnnotating = useMemo(() => {
    return isCurrentPeerSharing || isHost || isActingHost || isShowHideButtonEnabled;
  }, [isCurrentPeerSharing, isHost, isActingHost, isShowHideButtonEnabled]);

  // Compute whether there are any unread chat notifications,
  // showing public always, and private only when enabled.
  const hasChatNotifications = useMemo(() => {
    // how many public‐chat messages are unread?
    const publicNotificationsCount = messagesNotifications.get(EMessageType.PUBLIC)?.length ?? 0;

    // sum up *all* the non‐public peers’ unread messages
    const privateNotificationsCount = Array.from(messagesNotifications.entries())
      .filter(([peerId]) => peerId !== EMessageType.PUBLIC)
      .reduce((totalUnread, [_, messages]) => totalUnread + messages.length, 0);

    // show both when private chat is on, otherwise only public
    return isPrivateChatEnabled
      ? publicNotificationsCount + privateNotificationsCount > 0
      : publicNotificationsCount > 0;
  }, [isPrivateChatEnabled, messagesNotifications]);

  const getCurrentPeer = useCallback(() => {
    const socketId = SocketService.getSocketId();
    return peers.get(socketId || '');
  }, [peers]);

  const { emoji, setEmoji } = useEmojiStore(
    useShallow((state) => ({
      emoji: state.emoji,
      setEmoji: state.setEmoji,
    }))
  );

  const emojiTimerMapRef = useRef<Record<string, NodeJS.Timeout>>({});

  const handleRoomEmoji = useCallback(
    (cmd: ISocketCmd, duration = 5000) => {
      const { emoji, peer_id } = cmd;
      if (emoji && peer_id) {
        if (emojiTimerMapRef.current[peer_id]) {
          clearTimeout(emojiTimerMapRef.current[peer_id]);
        }

        setEmoji(undefined);

        updatePeerData(peer_id, { peer_emoji: emoji });

        emojiTimerMapRef.current[peer_id] = setTimeout(() => {
          updatePeerData(peer_id, { peer_emoji: undefined });
          delete emojiTimerMapRef.current[peer_id];
        }, duration);
      }
    },
    [updatePeerData, setEmoji]
  );

  const emitCmd = useCallback((cmd: ISocketCmd) => {
    SocketService.emit(ECommonSocketKeys.CMD, cmd);
  }, []);

  const handleCmd = useCallback(
    (cmd: ISocketCmd) => {
      switch (cmd.type) {
        case 'roomEmoji':
          handleRoomEmoji(cmd);
          setEmoji(cmd);
          break;
        default:
          break;
      }
    },
    [handleRoomEmoji]
  );

  const { peerAction, handleUpdatePeerInfo } = usePeerActions(currentPeer);

  useEffect(() => {
    let resizeDebounceTimeout: NodeJS.Timeout | null = null;

    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
      // Trigger overlay immediately on a resize event
      resizeDebounceTimeout && clearTimeout(resizeDebounceTimeout);
      resizeDebounceTimeout = setTimeout(() => {}, 1000);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      resizeDebounceTimeout && clearTimeout(resizeDebounceTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (isJoinedRoom && !!hasActiveLimitedPolls) {
      setSelectedKey('polls');
      toggleDrawer(true);
    }
  }, [isJoinedRoom, hasActiveLimitedPolls, setSelectedKey, toggleDrawer]);

  useEffect(() => {
    if (hasTempShareAccess && isCurrentPeerSharing) {
      setHasUsedShareAccess(true);
    }
  }, [canUserShareScreen, hasTempShareAccess, isCurrentPeerSharing]);

  useEffect(() => {
    if (hasUsedShareAccess && !currentPeer?.peer_info.peer_screen) {
      setHasTempShareAccess(false);
      setHasUsedShareAccess(false);

      //Reset request info here after the share is used so we can only have one request at a time for the speaker
      currentPeerType === EUserType.SPEAKER && useRoomStore.getState().resetRequestInfo();
    }
  }, [hasUsedShareAccess, currentPeer?.peer_info.peer_screen, currentPeerType]);

  //Fetch the configs for initial states of userTypeRoomConfig
  useEffect(() => {
    if (!isJoinedRoom) {
      return;
    }
    SocketService.request('getRoomConfigByUserType')
      .then((response) => {
        useRoomConfigStore.getState().setUserTypeRoomConfig(response);
      })
      .catch((err) => {
        console.error('getRoomConfigByUserType error:', err);
      });
  }, [isJoinedRoom]);

  // Cleanup emoji timers on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      Object.values(emojiTimerMapRef.current).forEach((timeout) => clearTimeout(timeout));
      emojiTimerMapRef.current = {};
    };
  }, []);

  // The useEffect was added because startScreenButton defaults to true and BE doesn’t send config for webinar yet.
  // Temp fix: in WEBINAR (speaker role) force it to false to enable Ask to share screen feature
  useEffect(() => {
    if (currentPeerType !== EUserType.SPEAKER) return;

    useRoomConfigStore.setState((state) => {
      if (state.userTypeRoomConfig.main.startScreenButton === false) return state;

      return {
        userTypeRoomConfig: {
          ...state.userTypeRoomConfig,
          main: {
            ...state.userTypeRoomConfig.main,
            startScreenButton: false,
          },
        },
      };
    });
  }, [currentPeerType]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      isFullyJoined,
      thereAreParticipants,
      currentPeerId,
      currentPeer,
      isHostOrCohost,
      isHost,
      isActingHost,
      isAttendee,
      isWebinar,
      isWebinarAttendee,
      isPanelist,
      currentPeerType,
      participantsPeers,
      isCurrentPeerSharing,
      shareScreenStream,
      canAnnotate,
      canStopStartAnnotating,

      hasChatNotifications,
      hasActiveLimitedPolls,
      setIsLoading,
      emitCmd,
      handleCmd,
      peerAction,
      getCurrentPeer,
      handleUpdatePeerInfo,

      emoji,
      isMobileDevice,
      isMobileAndTabletDevice,

      isLandscape,
      canUserRecord,
      isChatEmojiButtonEnabled,
      isChatFileSharingButtonEnabled,
      isChatSpeechStartButtonEnabled,

      isPublicChatEnabled,
      isPrivateChatEnabled,
      canUserShareScreen,
      isPollEnabled,
      canUserShareCalculator,

      canUserBan,
      canUserMute,
      canUserEject,
      canUserCreatePoll,
      hasTempShareAccess,
      hasRequestedShare,
      setHasRequestedShare,
      setHasTempShareAccess,
      setHasUsedShareAccess,

      isCallEnded,
      setIsCallEnded,
      canUserEndMeeting,
      canUserAskToUnmute,
      canUserManageSettings,
      micRequest,
      setMicRequest,
      camRequest,
      setCamRequest,
      processedMediaRequest,
      setProcessedMediaRequest,

      // refs
      recordedBlobsRef,
      isRecordingRef,
      audioRecorderRef,
      mediaRecorderRef,
      recScreenStreamRef,
      isAudioEventListenerActiveRef,

      // buttons visibility
      isStartAudioButtonEnabled,
      isStartVideoButtonEnabled,
      isTranscriptionButtonEnabled,
      isEmojiRoomButtonEnabled,
      isShareButtonEnabled,
      isRaiseHandButtonEnabled,
      isExitButtonEnabled,
      isEditorButtonEnabled,

      isWhiteboardButtonEnabled,
      isParticipantsButtonEnabled,
      isThreeDotsFooterMenuEnabled,
      isPollDeleteButtonEnabled,
      isCameraOptionsButtonEnabled,
      isMicOptionsButtonEnabled,
      isSpeakerOptionsButtonEnabled,
      isQuestionAndAnswerEnabled,

      speakersList,
      isCalculatorEnabled,
    }),
    [
      isLoading,
      isFullyJoined,
      thereAreParticipants,
      currentPeerId,
      currentPeer,
      isHostOrCohost,
      isHost,
      isActingHost,
      isAttendee,
      isWebinar,
      isWebinarAttendee,
      isPanelist,
      currentPeerType,
      participantsPeers,
      isCurrentPeerSharing,
      shareScreenStream,
      canAnnotate,
      canStopStartAnnotating,
      hasChatNotifications,
      hasActiveLimitedPolls,
      emitCmd,
      handleCmd,
      peerAction,
      getCurrentPeer,
      handleUpdatePeerInfo,
      emoji,
      isMobileDevice,
      isMobileAndTabletDevice,
      isLandscape,
      canUserRecord,
      isChatEmojiButtonEnabled,
      isChatFileSharingButtonEnabled,
      isChatSpeechStartButtonEnabled,
      isPublicChatEnabled,
      isPrivateChatEnabled,
      canUserShareScreen,
      isPollEnabled,
      canUserShareCalculator,
      canUserBan,
      canUserMute,
      canUserEject,
      canUserCreatePoll,
      hasTempShareAccess,
      hasRequestedShare,
      isCallEnded,
      canUserEndMeeting,
      canUserAskToUnmute,
      canUserManageSettings,
      micRequest,
      camRequest,
      processedMediaRequest,
      isStartAudioButtonEnabled,
      isStartVideoButtonEnabled,
      isTranscriptionButtonEnabled,
      isEmojiRoomButtonEnabled,
      isShareButtonEnabled,
      isRaiseHandButtonEnabled,
      isExitButtonEnabled,
      isEditorButtonEnabled,
      isWhiteboardButtonEnabled,
      isParticipantsButtonEnabled,
      isThreeDotsFooterMenuEnabled,
      isPollDeleteButtonEnabled,
      isCameraOptionsButtonEnabled,
      isMicOptionsButtonEnabled,
      isSpeakerOptionsButtonEnabled,
      isQuestionAndAnswerEnabled,
      speakersList,
      isCalculatorEnabled,
    ]
  );

  return <MeetRoomContext.Provider value={contextValue}>{children}</MeetRoomContext.Provider>;
};

export default MeetRoomContext;
